import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, ChevronLeft, Clock, Truck, CalendarDays } from 'lucide-react';
import { specialsAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function SpecialProductPage() {
  return (
    <AuthGate>
      <SpecialProductPageContent />
    </AuthGate>
  );
}

function SpecialProductPageContent() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Delivery date - minimum tomorrow
  const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };
  const [deliveryDate, setDeliveryDate] = useState(getTomorrow());
  const minDate = getTomorrow();

  const loadProduct = useCallback(async () => {
    setLoading(true);
    try {
      const res = await specialsAPI.getSpecialsCatalogItem(id);
      setProduct(res.data.item);
    } catch {
      navigate('/specials');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleAddToCart = async () => {
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }
    setAdding(true);
    try {
      await addToCart(product._id, quantity, 'specials', deliveryDate);
      toast.success(`${product.name} added to cart`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add to cart'));
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="pw-container py-10">
        <div className="animate-pulse">
          <div className="h-4 w-48 bg-[#f0eeeb] rounded mb-6" />
          <div className="grid md:grid-cols-2 gap-10">
            <div className="aspect-square bg-[#f0eeeb] rounded-xl" />
            <div className="space-y-4">
              <div className="h-3 w-20 bg-[#f0eeeb] rounded" />
              <div className="h-7 w-64 bg-[#f0eeeb] rounded" />
              <div className="h-4 w-full bg-[#f0eeeb] rounded" />
              <div className="h-4 w-3/4 bg-[#f0eeeb] rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const inStock = product.stock > 0;

  const breadcrumbs = [
    { label: 'Weekly Specials', href: '/specials' },
    { label: product.name }
  ];

  return (
    <div className="pw-container py-6">
      <Breadcrumbs items={breadcrumbs} />

      {/* Back link */}
      <Link
        to="/specials"
        data-testid="special-product-back"
        className="inline-flex items-center gap-1 text-xs text-[#6f6a63] hover:text-[#2d2d2d] transition-colors mb-6"
      >
        <ChevronLeft size={14} /> Back to Weekly Specials
      </Link>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Product Image */}
        <div className="relative overflow-hidden rounded-xl bg-[#f5f4f2]">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              data-testid="special-product-image"
              className="w-full h-auto object-contain"
            />
          ) : (
            <div className="w-full aspect-square flex items-center justify-center text-[#c0c0c0] text-sm">No image available</div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex flex-col">
          {product.category && (
            <span className="text-[10px] tracking-[0.15em] uppercase text-[#6f6a63] mb-2">{product.category}</span>
          )}

          <h1 data-testid="special-product-name" className="text-2xl font-semibold tracking-wider uppercase text-[#2d2d2d] mb-3">
            {product.name}
          </h1>

          {product.sku && (
            <p className="text-xs text-[#999] mb-3">SKU: {product.sku}</p>
          )}

          {/* Price */}
          {product.price > 0 && (
            <div data-testid="special-product-price" className="text-2xl font-semibold text-[#2d2d2d] mb-4">
              ${product.price.toFixed(2)}
              {product.pack_size && (
                <span className="text-sm font-normal text-[#6f6a63] ml-1">/ {product.pack_size}</span>
              )}
            </div>
          )}

          {/* Dispatch badge */}
          <div className="flex items-center gap-4 mb-5">
            <div className="flex items-center gap-1.5 bg-[#e7f1ea] text-[#2f6b4f] px-3 py-1.5 rounded-full text-xs font-medium">
              <Truck size={13} />
              Dispatch within 48 hours
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#6f6a63]">
              <Clock size={13} />
              Quick Sale
            </div>
          </div>

          {/* Color */}
          {product.color && (
            <div className="mb-4">
              <span className="text-xs text-[#6f6a63]">Color: </span>
              <span className="text-xs font-medium text-[#2d2d2d]">{product.color}</span>
            </div>
          )}

          {/* Description */}
          {product.description && (
            <p data-testid="special-product-description" className="text-sm text-[#6f6a63] leading-relaxed mb-6">
              {product.description}
            </p>
          )}

          {/* Stock */}
          <div className="mb-4">
            {inStock ? (
              <span className="text-xs text-[#2f6b4f] font-medium">In Stock ({product.stock} available)</span>
            ) : (
              <span className="text-xs text-[#c0c0c0]">Stock not specified</span>
            )}
          </div>

          {/* Delivery Date */}
          <div className="mb-6 p-3 bg-[#fbfaf8] rounded-lg border border-[#e7e3dd]">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays size={15} className="text-[#2f6b4f]" />
              <span className="text-xs font-semibold text-[#2d2d2d]">Delivery Date</span>
            </div>
            <input
              data-testid="special-product-delivery-date"
              type="date"
              value={deliveryDate}
              min={minDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] cursor-pointer w-full"
            />
            <p className="text-[10px] text-[#6f6a63] mt-1.5">Next-day delivery or later. Same-day not available.</p>
          </div>

          {/* Quantity & Add to Cart */}
          <div className="flex items-center gap-4 mt-auto">
            <div className="flex items-center border border-[#e7e3dd] rounded-lg">
              <button
                data-testid="special-product-qty-minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2.5 hover:bg-[#fbfaf8] transition-colors rounded-l-lg"
              >
                <Minus size={16} className="text-[#6f6a63]" />
              </button>
              <span data-testid="special-product-qty" className="w-12 text-center text-sm font-medium">{quantity}</span>
              <button
                data-testid="special-product-qty-plus"
                onClick={() => setQuantity(quantity + 1)}
                className="p-2.5 hover:bg-[#fbfaf8] transition-colors rounded-r-lg"
              >
                <Plus size={16} className="text-[#6f6a63]" />
              </button>
            </div>

            <button
              data-testid="special-product-add-to-cart"
              onClick={handleAddToCart}
              disabled={adding}
              className="flex-1 flex items-center justify-center gap-2 bg-[#2d2d2d] text-white py-3 rounded-lg text-sm font-medium hover:bg-[#232323] disabled:opacity-50 transition-colors"
            >
              <ShoppingCart size={16} />
              {adding ? 'Adding...' : 'Add to Cart'}
            </button>
          </div>

          {/* Total line */}
          {product.price > 0 && (
            <div className="mt-3 text-xs text-[#6f6a63]">
              Total: <span className="font-medium text-[#2d2d2d]">${(product.price * quantity).toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
